import React, { useState, useCallback, useRef } from 'react';
import { Eye, Edit, Trash2, Plus } from 'lucide-react';
import { Button } from '../../../../../../components/common/Button';
import { formatDistance } from '../../../../../../utils/routeHelpers';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../../../config/firebase';
import { logError, logInfo } from '../../../../../../utils/logger';

const RoutesTab = ({ map, onVisibilityChange, visibilityState = {}, routes = [], activeSurvey }) => {
  const [editingRoute, setEditingRoute] = useState(null);
  const mountedRef = useRef(true);
  const [loading, setLoading] = useState(false);

  const handleVisibilityToggle = useCallback((routeId) => {
    if (onVisibilityChange) {
      const currentVisibility = visibilityState?.routes?.[routeId] !== false;
      onVisibilityChange('route', {
        id: routeId,
        visible: !currentVisibility
      });
      logInfo(`Route visibility toggled for route ${routeId}`);
    }
  }, [onVisibilityChange, visibilityState]);

  const handleDeleteRoute = useCallback(async (routeId) => {
    if (!window.confirm('Are you sure you want to delete this route?')) return;
    
    try {
      const routeRef = doc(db, 'routes', routeId);
      await updateDoc(routeRef, {
        deleted: true,
        updatedAt: new Date().toISOString()
      });
      logInfo('Route deleted successfully');
    } catch (error) {
      logError('Failed to delete route:', error);
    }
  }, []);

  console.log('[RoutesTab] Rendering with:', {
    routesCount: routes?.length,
    hasActiveSurvey: !!activeSurvey
  });

  if (!activeSurvey) {
    return (
      <div className="p-4 text-center text-gray-400">
        No active survey selected
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-medium text-white">Routes</h2>
      
      <Button
        onClick={() => onVisibilityChange('routePlanner', true)}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2 p-3 rounded-lg"
      >
        <Plus className="w-4 h-4" />
        <span>Create New Route</span>
      </Button>

      <div className="space-y-2">
        {routes.map((route) => (
          <div
            key={route.id}
            className="bg-gray-700/50 p-3 rounded-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: route.color || '#3B82F6' }}
                />
                <div>
                  <div className="text-white">{route.name || `Route ${route.id.slice(-12)}`}</div>
                  <div className="text-sm text-gray-400">
                    {route.trackPoints?.length || 0} points • {formatDistance(route.distance)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleVisibilityToggle(route.id)}
                  className={`p-2 rounded-lg transition-colors hover:bg-gray-600/30 ${
                    visibilityState.routes?.[route.id] === false
                      ? 'bg-gray-600/20 text-gray-400 hover:bg-gray-600/30'
                      : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                  }`}
                  title={visibilityState.routes?.[route.id] === false ? 'Show route' : 'Hide route'}
                >
                  <Eye className={`w-4 h-4 ${visibilityState.routes?.[route.id] !== false ? "text-red-400" : "text-gray-400"}`} />
                </Button>
                <Button
                  onClick={() => setEditingRoute(route)}
                  className="p-2 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 rounded-lg transition-colors"
                  title="Edit route"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => handleDeleteRoute(route.id)}
                  className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors"
                  title="Delete route"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoutesTab;